/**
 * Inline filter panel container for the Add Player to List modal
 *
 * Uses useFilterPanelLogic for all shared filter logic (draft state,
 * section toggles, chip dropdowns, counts, callbacks). Adds
 * custom metrics lookup via useAvailableCustomMetrics.
 * @module components/shared/search-modal/InlineFilterPanel/InlineFilterPanelContainer
 */

import { useMemo } from "react";

import InlineFilterPanelView from "./InlineFilterPanelView";

import type { FilterOption } from "@/types/filter";

import { useAvailableCustomMetrics } from "@/hooks/useAvailableCustomMetrics";
import type { FilterState } from "@/hooks/useFilterState";
import useFilterPanelLogic from "@/features/scouting/hooks/useFilterPanelLogic";

/** Props for InlineFilterPanelContainer */
interface InlineFilterPanelContainerProps {
  /** Filter state from useFilterState */
  readonly filterState: FilterState;
  /** Derived position filter options */
  readonly positionOptions: readonly FilterOption[];
  /** Derived club filter options */
  readonly clubOptions: readonly FilterOption[];
  /** Optional override for closing the panel — used by GlobalSearchModal to close via its own reactive variable */
  readonly onClose?: () => void;
}

/**
 * Container for the inline filter panel — uses useFilterPanelLogic
 * for shared logic and adds custom metrics lookup.
 * @param root0 - Component props
 * @param root0.filterState - Filter state hook return value
 * @param root0.positionOptions - Position filter options
 * @param root0.clubOptions - Club filter options
 * @param root0.onClose - Optional override for closing the panel
 */
const InlineFilterPanelContainer = ({
  filterState,
  positionOptions,
  clubOptions,
  onClose,
}: InlineFilterPanelContainerProps) => {
  const panelLogic = useFilterPanelLogic({ filterState, onClose });

  const { availableCustomMetrics } = useAvailableCustomMetrics();

  const customMetrics = useMemo(
    () =>
      availableCustomMetrics.map(m => ({
        id: m.id,
        displayName: m.displayName,
      })),
    [availableCustomMetrics]
  );

  return (
    <InlineFilterPanelView
      activeFilterCount={panelLogic.activeFilterCount}
      onClearAll={panelLogic.clearDraft}
      sectionCounts={panelLogic.sectionCounts}
      expandedSections={panelLogic.expandedSections}
      sectionToggleHandlers={panelLogic.sectionToggleHandlers}
      onCancel={panelLogic.handleCancel}
      onShowResults={panelLogic.handleShowResults}
      roleOptions={filterState.roleOptions}
      selectedRoleArchetypes={panelLogic.draft.selectedRoleArchetypes}
      onToggleRole={panelLogic.handleToggleRole}
      positionOptions={positionOptions}
      selectedPositionIds={panelLogic.draft.selectedPositionIds}
      onTogglePosition={panelLogic.handleTogglePosition}
      onClearPositions={panelLogic.handleClearPositions}
      isPositionFilterOpen={panelLogic.openChipId === "position"}
      onTogglePositionFilter={panelLogic.onTogglePositionFilter}
      onClosePositionFilter={panelLogic.onCloseChip}
      onServerSearchClub={filterState.setClubSearchText}
      clubSearching={filterState.clubSearching}
      clubOptions={clubOptions}
      selectedClubIds={panelLogic.draft.selectedClubIds}
      onToggleClub={panelLogic.handleToggleClub}
      onClearClubs={panelLogic.handleClearClubs}
      isClubFilterOpen={panelLogic.openChipId === "club"}
      onToggleClubFilter={panelLogic.onToggleClubFilter}
      onCloseClubFilter={panelLogic.onCloseChip}
      leagueOptions={filterState.leagueOptions}
      selectedLeagueIds={panelLogic.draft.selectedLeagueIds}
      onToggleLeague={panelLogic.handleToggleLeague}
      onClearLeagues={panelLogic.handleClearLeagues}
      isLeagueFilterOpen={panelLogic.openChipId === "league"}
      onToggleLeagueFilter={panelLogic.onToggleLeagueFilter}
      onCloseLeagueFilter={panelLogic.onCloseChip}
      onChangeLeaguePreset={panelLogic.handleSetLeagueIds}
      competitionOptions={filterState.competitionOptions}
      competitionOptionsLoading={filterState.competitionOptionsLoading}
      selectedCompetitionIds={panelLogic.draft.selectedCompetitionIds}
      onToggleCompetition={panelLogic.handleToggleCompetition}
      onClearCompetitions={panelLogic.handleClearCompetitions}
      isCompetitionFilterOpen={panelLogic.openChipId === "competition"}
      onToggleCompetitionFilter={panelLogic.onToggleCompetitionFilter}
      onCloseCompetitionFilter={panelLogic.onCloseChip}
      nationalityOptions={filterState.nationalityOptions}
      selectedNationalityIds={panelLogic.draft.selectedNationalityIds}
      onToggleNationality={panelLogic.handleToggleNationality}
      onClearNationalities={panelLogic.handleClearNationalities}
      isNationalityFilterOpen={panelLogic.openChipId === "nationality"}
      onToggleNationalityFilter={panelLogic.onToggleNationalityFilter}
      onCloseNationalityFilter={panelLogic.onCloseChip}
      ageRange={panelLogic.draft.ageRange}
      onSetAgeRange={panelLogic.handleSetAgeRange}
      contractMonthsRange={panelLogic.draft.contractMonthsRange}
      onSetContractMonthsRange={panelLogic.handleSetContractMonthsRange}
      valuationRange={panelLogic.draft.valuationRange}
      onSetValuationRange={panelLogic.handleSetValuationRange}
      gprRange={panelLogic.draft.gprRange}
      onSetGprRange={panelLogic.handleSetGprRange}
      fitScoreRange={panelLogic.draft.fitScoreRange}
      onSetFitScoreRange={panelLogic.handleSetFitScoreRange}
      teamStyleFitRange={panelLogic.draft.teamStyleFitRange}
      onSetTeamStyleFitRange={panelLogic.handleSetTeamStyleFitRange}
      carryingRange={panelLogic.draft.carryingRange}
      onSetCarryingRange={panelLogic.handleSetCarryingRange}
      dribblingRange={panelLogic.draft.dribblingRange}
      onSetDribblingRange={panelLogic.handleSetDribblingRange}
      passingRange={panelLogic.draft.passingRange}
      onSetPassingRange={panelLogic.handleSetPassingRange}
      shootingRange={panelLogic.draft.shootingRange}
      onSetShootingRange={panelLogic.handleSetShootingRange}
      gkPassingRange={panelLogic.draft.gkPassingRange}
      onSetGkPassingRange={panelLogic.handleSetGkPassingRange}
      gkShotStoppingRange={panelLogic.draft.gkShotStoppingRange}
      onSetGkShotStoppingRange={panelLogic.handleSetGkShotStoppingRange}
      gkPositioningRange={panelLogic.draft.gkPositioningRange}
      onSetGkPositioningRange={panelLogic.handleSetGkPositioningRange}
      customMetrics={customMetrics}
      customStatRanges={panelLogic.draft.customStatRanges}
      onSetCustomStatRange={panelLogic.handleSetCustomStatRange}
    />
  );
};

export default InlineFilterPanelContainer;
